// pages/features/AlertsFeaturePage.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import HomeHeader from "../modals/HomeHeader.js";
import { useCurrentUser } from "../APIs/UserContext";
import keycloak from "../APIs/keycloak";

export default function AlertsFeaturePage() {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  const handleCreateAlert = () => {
    if (currentUser) {
      navigate("/CreateProviderAlert");
    } else {
      keycloak.login({
        redirectUri: window.location.origin + "/CreateProviderAlert",
      });
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <HomeHeader />

      {/* HERO */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Alerts &amp; Notifications
            </h1>

            <p className="mt-3 text-lg text-gray-600 leading-relaxed">
              Stay informed without constantly checking for availability—
              MedMaze notifies you when the right providers become available.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Pill label="Availability Alerts" accent="emerald" />
              <Pill label="Specialty-Based" />
              <Pill label="Location-Aware" />
              <Pill label="Real-Time" accent="teal" />
            </div>

            <div className="mt-8">
              <button
                type="button"
                onClick={handleCreateAlert}
                className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
              >
                Sign In to Create An Alert →
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Alerts are triggered automatically when criteria are met.
              </p>
            </div>
          </div>

          {/* Notification Feed Illustration */}
          <div className="rounded-2xl border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-800">
                  Notifications
                </span>
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] rounded-full bg-teal-600 px-1.5 text-[10px] font-bold text-white">
                  3
                </span>
              </div>
              <div className="text-xs text-gray-400">Today</div>
            </div>

            <div className="divide-y divide-gray-100">
              {/* Notification 1 — New Availability (unread) */}
              <div className="px-5 py-3.5 flex gap-3 bg-teal-50/50">
                <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-sm">📅</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      New availability opened
                    </p>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-teal-500" />
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Dr. Amina Johnson has a new opening on{" "}
                    <span className="font-semibold">
                      Sun, Feb 15 at 2:00 PM
                    </span>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">12 min ago</p>
                </div>
              </div>

              {/* Notification 2 — Message Received (unread) */}
              <div className="px-5 py-3.5 flex gap-3 bg-teal-50/50">
                <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-sm">💬</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      New message from provider
                    </p>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-teal-500" />
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Dr. Samuel Okafor:{" "}
                    <span className="text-gray-500">
                      "Your lab results are ready to review..."
                    </span>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">1 hr ago</p>
                </div>
              </div>

              {/* Notification 3 — Appointment Reminder (unread) */}
              <div className="px-5 py-3.5 flex gap-3 bg-teal-50/50">
                <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-sm">🔔</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">
                      Appointment reminder
                    </p>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-teal-500" />
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Your visit with{" "}
                    <span className="font-semibold">Dr. Michelle Chen</span> is
                    tomorrow at <span className="font-semibold">10:00 AM</span>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">3 hrs ago</p>
                </div>
              </div>

              {/* Notification 4 — Waitlist Update (read) */}
              <div className="px-5 py-3.5 flex gap-3">
                <div className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-sm">⏳</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-600">
                    Waitlist position updated
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    You moved from #5 to #2 for Dr. Amina Johnson
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">Yesterday</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INFORMATION THAT COMES TO YOU — full-width teal gradient */}
      <section className="bg-gradient-to-br from-teal-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Information that comes to you
          </h2>
          <div className="mt-4 space-y-3 text-gray-700 leading-relaxed">
            <p>
              Patients shouldn't have to constantly refresh or search to
              discover provider availability. With MedMaze, patients can set
              alerts based on specific preferences—such as specialty, location,
              or time window—and get notified when matching providers become
              available.
            </p>
            <p>
              Alerts ensure patients stay informed automatically, allowing them
              to make timely, confident decisions without unnecessary effort.
            </p>
          </div>
        </div>
      </section>

      {/* PATIENT + PROVIDER BENEFITS */}
      <section className="bg-white py-4 pb-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Patient panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              Designed for patient peace of mind
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <BulletIcon />
                <span>Set alerts for specific provider criteria.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>Get notified when availability opens up.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Discover relevant providers without constant searching.
                </span>
              </li>
            </ul>
          </div>

          {/* Provider panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              Efficient discovery for providers
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700">
              <li className="flex gap-3">
                <BulletIcon />
                <span>Connect with patients actively seeking your care.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Reduce missed opportunities from last-minute availability.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Improve alignment between availability and patient intent.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <h3 className="text-lg font-bold text-gray-900">
              Why alerts matter
            </h3>
            <p className="mt-2 text-sm text-gray-700">
              By turning availability into notifications, MedMaze removes
              friction from discovery—so patients stay informed and providers
              stay visible at exactly the right moment.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-8 text-xs text-gray-500 text-center border-t">
        © {new Date().getFullYear()} MedMaze. All rights reserved.
      </footer>
    </div>
  );
}

function Pill({ label, accent }) {
  const base =
    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold";
  const theme =
    accent === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : accent === "teal"
        ? "border-teal-300 bg-teal-50 text-teal-700"
        : "border-teal-200 bg-teal-50 text-teal-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function BulletIcon() {
  return (
    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
      ✓
    </span>
  );
}
